/**
 * Progressive disclosure for the operator dashboard (gap G20).
 *
 * A new operator opens the dashboard and meets twenty-four capabilities at
 * once: spin wheels, journeys, segments, achievements, promotions, tags. They
 * have not washed a car yet. Choice overload on the activation path is the
 * cost, and the features that matter on day one — take a booking, take money,
 * set up your prices — are the ones buried among the rest.
 *
 * ## Gated on READINESS, not on time
 *
 * Nothing here unlocks on a timer, because a timer is a guess about someone
 * else's business dressed up as a feature. A capability appears when it can
 * actually do something: loyalty when there are customers to be loyal, marketing
 * when there is someone to market to. Before that it is not "locked", it is
 * empty — and an empty screen teaches nothing.
 *
 * ## Three rules that outrank the staging
 *
 * Hiding is a real cost, so it loses every argument against these:
 *
 *   1. **ALWAYS_VISIBLE never hides.** Running the business (queue, till),
 *      fixing what is wrong (catalog, outlets, staff, hours) and money
 *      (billing) are permanently present. Hiding where someone pays you — or
 *      where they see what you charged them — would be indefensible whatever
 *      it did for tidiness.
 *   2. **Anything already in use stays.** If an operator made a voucher, the
 *      voucher screen is theirs, whatever stage says. Taking away something
 *      someone has used is not disclosure, it is loss.
 *   3. **The operator can switch it off.** `showEverything` reveals all of it,
 *      permanently, in one tap. Our guess about their readiness is not better
 *      than their knowledge of their own business.
 *
 * Pure and total: no I/O, no clock. The caller supplies the signals.
 */

/** How far along an operator is, derived from what they have actually done. */
export type DisclosureStage =
  /** No washes yet. Get them set up and taking bookings. */
  | 'setup'
  /** Washing cars. They now have takings and customers worth looking at. */
  | 'serving'
  /** Enough repeat customers that loyalty is not an empty room. */
  | 'retaining'
  /** Everything, reached by using the product rather than by waiting. */
  | 'growing';

/** What the operator has actually done. */
export interface DisclosureSignals {
  /** Washes recorded, ever. */
  readonly washesRecorded: number;
  /** Distinct customers on the books. */
  readonly customersKnown: number;
  /** Whether they have set up any loyalty mechanic at all. */
  readonly loyaltyConfigured: boolean;
  /** The operator asked to see everything. Overrides every stage. */
  readonly showEverything: boolean;
}

/**
 * Capabilities that are NEVER hidden.
 *
 * Three kinds: the ones that run the business, the ones that let someone fix or
 * understand what is happening to their money, and the ones that teach — a
 * learning path hidden from a struggling operator is the worst possible thing
 * to hide, since needing help is exactly the state it exists for. A dashboard that hides
 * billing from a confused operator has stopped being a tool and started being a
 * funnel.
 */
export const ALWAYS_VISIBLE: readonly string[] = [
  'queue',
  'pos',
  'catalog',
  'outlets',
  'staff',
  'hours',
  'billing',
  'help',
  'academy',
  'printQr',
];

/** The stage at which each gated capability first appears. */
const UNLOCKS_AT: Readonly<Record<string, DisclosureStage>> = {
  // Worth looking at the moment there is anything to look at.
  customers: 'serving',
  reports: 'serving',
  refunds: 'serving',
  products: 'serving',
  reviews: 'serving',
  // Sharing the shop is most useful EARLY — a new carwash needs its first
  // customers more than anyone — so the Share kit appears with the first wash
  // rather than waiting for the marketing tools.
  shareKit: 'serving',

  // Loyalty needs somebody to be loyal.
  loyalty: 'retaining',
  vouchers: 'retaining',
  achievements: 'retaining',
  challenges: 'retaining',
  spinWheel: 'retaining',
  promotions: 'retaining',
  tags: 'retaining',

  // Marketing needs an audience, and an audience worth segmenting.
  marketing: 'growing',
  segments: 'growing',
  journeys: 'growing',
  map: 'growing',
};

const ORDER: readonly DisclosureStage[] = ['setup', 'serving', 'retaining', 'growing'];

/** Thresholds, deliberately low: this is about emptiness, not achievement. */
export const SERVING_AFTER_WASHES = 1;
export const RETAINING_AFTER_CUSTOMERS = 10;
export const GROWING_AFTER_CUSTOMERS = 30;

/**
 * Which stage the operator's own activity puts them in.
 *
 * `showEverything` short-circuits to the last stage rather than being handled
 * by every caller, so there is one place the override can be forgotten: here,
 * where it is not.
 */
export function disclosureStage(signals: DisclosureSignals): DisclosureStage {
  if (signals.showEverything) return 'growing';

  const washes = Number.isFinite(signals.washesRecorded) ? signals.washesRecorded : 0;
  const customers = Number.isFinite(signals.customersKnown) ? signals.customersKnown : 0;

  if (customers >= GROWING_AFTER_CUSTOMERS) return 'growing';
  // Configuring loyalty is itself a declaration of intent — someone who set up
  // a voucher scheme has told us they are thinking about retention, and should
  // not have to wait for a customer count to see the rest of it.
  if (customers >= RETAINING_AFTER_CUSTOMERS || signals.loyaltyConfigured) return 'retaining';
  if (washes >= SERVING_AFTER_WASHES) return 'serving';
  return 'setup';
}

/** Whether `stage` has reached `required`. */
function reached(stage: DisclosureStage, required: DisclosureStage): boolean {
  return ORDER.indexOf(stage) >= ORDER.indexOf(required);
}

/**
 * Whether one capability should be visible.
 *
 * `inUse` is the second rule made explicit: a caller that knows the operator has
 * data behind a capability passes true, and it stays visible regardless of
 * stage. Removing something someone has already used is not disclosure.
 */
export function isUnlocked(capabilityKey: string, stage: DisclosureStage, inUse = false): boolean {
  if (ALWAYS_VISIBLE.includes(capabilityKey)) return true;
  if (inUse) return true;

  const required = UNLOCKS_AT[capabilityKey];
  // An unknown capability is VISIBLE. Failing open matters: a screen added
  // later and never listed here would otherwise vanish silently, and nobody
  // would notice until an operator asked where it went.
  if (!required) return true;

  return reached(stage, required);
}

/**
 * Filter a list of capability keys for one stage.
 *
 * Keeps the caller's order — the nav's own grouping is a design decision this
 * has no business rearranging.
 */
export function visibleCapabilities(
  keys: readonly string[],
  stage: DisclosureStage,
  inUse: ReadonlySet<string> = new Set(),
): string[] {
  return keys.filter((k) => isUnlocked(k, stage, inUse.has(k)));
}

/**
 * How many capabilities are currently hidden.
 *
 * For telling the operator the truth in one line — "6 more appear as you go" —
 * rather than letting them wonder whether the product is missing things.
 */
export function hiddenCount(keys: readonly string[], stage: DisclosureStage): number {
  return keys.length - visibleCapabilities(keys, stage).length;
}
